import { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const API_URL = import.meta.env.VITE_API_URL;

type City = {
  citiesId: number;
  name: string;
  priority: boolean;
};

export default function Cities(): JSX.Element {
  const [input, setInput] = useState<string>('');
  const [priority, setPriority] = useState<boolean>(false);

  const [cities, setCities] = useState<City[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [sortField, setSortField] = useState<'name' | 'priority'>('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [snackbar, setSnackbar] = useState<{
    text: string;
    color: 'success' | 'error';
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const isMultiple = input.includes(',');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCities = async (): Promise<void> => {
    const res = await fetch(
      `${API_URL}/city?page=${page + 1}&limit=${rowsPerPage}&field=${sortField}&order=${sortOrder}&search=${debouncedSearch}`,
    );
    const data = await res.json();
    setCities(data.data);
    setTotal(data.total);
  };

  useEffect(() => {
    fetchCities();
  }, [page, rowsPerPage, sortField, sortOrder, debouncedSearch]);

  const handleCreate = async (): Promise<void> => {
    if (!isMultiple) {
      const res = await fetch(`${API_URL}/cities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: input.trim(), priority }),
      });

      const data = await res.json();

      if (!res.ok) {
        setSnackbar({ text: data.error, color: 'error' });
        return;
      }

      setCities((prev) => [data.data, ...prev]);
      setInput('');
      return;
    }

    const names = input.split(',').map((n) => n.trim());

    const res = await fetch(`${API_URL}/cities/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names, priority }),
    });

    const data = await res.json();

    if (!res.ok) {
      setSnackbar({ text: 'Помилка', color: 'error' });
      return;
    }

    if (data.skipped.length === 0) {
      setSnackbar({ text: 'Всі міста додані успішно', color: 'success' });
    } else {
      setSnackbar({
        text: `Додані всі міста крім - ${data.skipped.join(', ')}. Вони вже є в адмін панелі`,
        color: 'error',
      });
    }

    fetchCities();
    setInput('');
  };

  const handleUpdate = async (
    citiesId: number,
    field: 'name' | 'priority',
    value: string | boolean,
  ): Promise<void> => {
    const prev = [...cities];

    setCities((list) =>
      list.map((c) => (c.citiesId === citiesId ? { ...c, [field]: value } : c)),
    );

    const res = await fetch(`${API_URL}/cities/${citiesId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });

    if (!res.ok) {
      setCities(prev);
      setSnackbar({ text: 'Помилка оновлення', color: 'error' });
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleteId === null) return;

    const prev = [...cities];

    setCities((list) => list.filter((c) => c.citiesId !== deleteId));
    setDeleteId(null);

    const res = await fetch(`${API_URL}/cities/${deleteId}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      setCities(prev);
      setSnackbar({ text: 'Помилка видалення', color: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="center" mb={4}>
        <Paper sx={{ p: 3, width: 500 }}>
          <Typography mb={1}>Назва міста</Typography>

          <TextField
            fullWidth
            multiline
            minRows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />

          <Typography variant="caption" color="text.secondary">
            Ви можете додати декілька міст одразу вказавши їх через кому.
            Наприклад: Київ, Львів
          </Typography>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={priority}
                onChange={(e) => setPriority(e.target.checked)}
              />
            }
            label={
              isMultiple
                ? 'Показувати користувачу в боті всі міста пріорітетно'
                : 'Показувати користувачу в боті це місто пріорітетно'
            }
          />

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleCreate}
          >
            {isMultiple ? 'Додати міста' : 'Додати місто'}
          </Button>
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Пошук міст..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel>Сортування</InputLabel>
          <Select
            label="Сортування"
            value={`${sortField}_${sortOrder}`}
            onChange={(e) => {
              const [field, order] = String(e.target.value).split('_');
              setSortField(field as 'name' | 'priority');
              setSortOrder(order as 'ASC' | 'DESC');
              setPage(0);
            }}
          >
            <MenuItem value="name_ASC">Назва (А–Я)</MenuItem>
            <MenuItem value="name_DESC">Назва (Я–А)</MenuItem>
            <MenuItem value="priority_ASC">Пріоритетні</MenuItem>
            <MenuItem value="priority_DESC">Не пріоритетні</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell>Пріоритет</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {cities.map((city) => (
            <TableRow key={city.citiesId}>
              <TableCell>
                <TextField
                  value={city.name}
                  onChange={(e) =>
                    handleUpdate(city.citiesId, 'name', e.target.value)
                  }
                  size="small"
                />
              </TableCell>

              <TableCell>
                <Checkbox
                  checked={city.priority}
                  onChange={(e) =>
                    handleUpdate(city.citiesId, 'priority', e.target.checked)
                  }
                />
              </TableCell>

              <TableCell>
                <IconButton onClick={() => setDeleteId(city.citiesId)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage: number) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar?.text}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.color === 'success' ? 'green' : 'red',
          },
        }}
      />

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Видалити місто?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Скасувати</Button>
          <Button color="error" onClick={confirmDelete}>
            Видалити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
